import { useState, useEffect, useRef } from "react";

/**
 * События на которые реагирует хук
 */
const EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "touchmove",
  "scroll",
];

/**
 * Кол-во миллисекунд, которое должно пройти после последнего взаимодействия пользователя с элементом
 */
const TIMEOUT = 3000;

/**
 * Хук позволяет определить взаимодействует ли пользователь с DOM элементом.
 * idle === true когда пользователь бездействует
 *
 * Надо помнить про особенности поведения событий в Safari на IOS.
 * Если на onmousemove меняется контент страницы, то клик не случается.
 * Поэтому все кликабельные элемены НЕ ДОЛЖНЫ лежать внутри элемента target.
 * Иначе при первом клике на них ни чего не произойдет, а только значение idle поменяется.
 * Подробнее об этом: https://developer.apple.com/library/archive/documentation/AppleApplications/Reference/SafariWebContent/HandlingEvents/HandlingEvents.html
 *
 * @param {Object} params
 * @param {String} params.ref - React.ref элемена на который навешивать события
 * @returns {Boolean}
 */
export default function useIdleRef({ ref }) {
  const [idle, setIdle] = useState(true);
  const timeout = useRef();

  const el = ref.current;
  const isBrowser = process.browser;

  useEffect(() => {
    if (!isBrowser) {
      return;
    }

    if (!el) {
      return;
    }

    const handleEvent = (event) => {
      if (idle) {
        event.stopPropagation();
        event.preventDefault();
        setIdle(false);
      }
      clearTimeout(timeout.current);
      setIdleTimeout();
    };

    const setIdleTimeout = () => {
      timeout.current = setTimeout(() => {
        setIdle(true);
      }, TIMEOUT);
    };

    EVENTS.forEach((event) => el.addEventListener(event, handleEvent));

    setIdleTimeout();

    return () => {
      EVENTS.forEach((event) => el.removeEventListener(event, handleEvent));

      clearTimeout(timeout.current);
    };
  }, [el, idle, isBrowser]);

  return idle;
}
